/**
 * A subject's one-line summary and chapter strip on the Subjects home (design v2 screen 4),
 * built from what the subject screen already reads — documents, papers, a ready book's
 * chapters — and this browser's studied list. Nothing here is a number the API did not send.
 */
import type { ChapterWire, Doc, PaperRow } from "../api/api";

export type Pill = { kind: "check" | "failed"; label: string };
export type Summary = { pill: Pill | null; line: string };
export type Cell = "studied" | "ingested" | "not";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const WORDS = ["No", "One", "Two", "Three", "Four", "Five"];

export function summarise({
  docs,
  papers,
  chapters,
  studied,
}: {
  docs: Doc[];
  papers: PaperRow[];
  chapters: ChapterWire[];
  studied: number;
}): Summary {
  if (docs.length === 0)
    return { pill: null, line: "No documents yet. Upload the textbook to begin." };
  const books = docs.filter((d) => d.kind === "textbook" && d.status === "uploaded");
  const pastPapers = docs.filter((d) => d.kind === "past_paper" && d.status === "uploaded");

  const failed = books.find((d) => d.book_status === "failed");
  if (failed)
    return {
      pill: { kind: "failed", label: "Needs attention" },
      line: "The flashcards could not be finished; the pages were not charged.",
    };

  const ready = books.some((d) => d.book_status === "ready");
  const review = books.filter((d) => d.book_status === "review");
  if (review.length > 0 && !ready) {
    const pages = review.reduce((n, d) => n + (d.page_count ?? 0), 0);
    const count = WORDS[review.length] ?? String(review.length);
    return {
      pill: { kind: "check", label: "Chapters need checking" },
      line: `${count} textbook${review.length === 1 ? "" : "s"}, ${pages} pages. Nothing has been spent yet.`,
    };
  }

  const parts: string[] = [];
  if (ready && chapters.length > 0) parts.push(`${studied} of ${chapters.length} chapters studied`);
  const written = papers.filter((p) => p.status === "ready").length;
  if (written > 0) parts.push(plural(written, "paper"));
  const making = books.filter(
    (d) => d.book_status === "confirmed" || d.book_status === "ingesting",
  ).length;
  if (making > 0) parts.push(`${plural(making, "book")} making flashcards`);
  if (review.length > 0) parts.push(`${plural(review.length, "book")} to check`);
  if (!ready && pastPapers.length > 0) parts.push(plural(pastPapers.length, "past paper"));
  if (parts.length === 0) parts.push(plural(docs.length, "document"));
  return { pill: null, line: parts.join(" · ") };
}

/** One cell per chapter of the book, in order. */
export function strip(chapters: ChapterWire[], studied: number[]): Cell[] {
  const seen = new Set(studied);
  return [...chapters]
    .sort((a, b) => a.number - b.number)
    .map((c) => (seen.has(c.number) ? "studied" : c.ingested_at ? "ingested" : "not"));
}
